import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import client, { auth } from "@/lib/api";

export default function AdminLogin() {
  const [email, setEmail] = useState("alex@weddingvideo.co.uk");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const nav = useNavigate();

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await client.post("/auth/login", {
        email,
        password,
        remember,
        otp_code: !useRecovery && otpCode ? otpCode : undefined,
        recovery_code: useRecovery && otpCode ? otpCode : undefined,
      });
      auth.markAdmin();
      toast.success("Welcome back");
      nav("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "OTP_REQUIRED") {
        setOtpRequired(true);
        toast.info("New device: enter your Google Authenticator code");
      } else {
        toast.error(detail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen w-full bg-[#FAF9F6] flex flex-col" data-testid="admin-login-page">
      <header className="px-6 md:px-12 lg:px-24 py-8 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-overline uppercase text-[#5C5A56] hover:text-[#1F1E1D]">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Back
        </Link>
        <div>
          <div className="text-xs tracking-overline uppercase text-[#8A8782]">
            Wedding Video
          </div>
          <div className="font-display text-2xl leading-none tracking-tight text-[#2F2E2E]">
            Essex
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md fade-up">
          <div className="text-xs tracking-overline uppercase text-[#A9927D] mb-4 font-medium">
            Admin Portal
          </div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-none mb-10 text-[#1F1E1D]">
            Sign in.
          </h1>

          <form onSubmit={onSubmit} className="space-y-8" data-testid="admin-login-form">
            <div>
              <label className="block text-xs tracking-overline uppercase text-[#8A8782] mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="admin-email-input"
                className="w-full border-b border-[#D1CEC2] bg-transparent text-lg py-3 focus:border-[#2F2E2E] outline-none transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs tracking-overline uppercase text-[#8A8782] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-password-input"
                  className="w-full border-b border-[#D1CEC2] bg-transparent text-lg py-3 pr-12 focus:border-[#2F2E2E] outline-none transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  data-testid="admin-password-toggle"
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#8A8782] hover:text-[#1F1E1D] p-2"
                  aria-label="Toggle password visibility"
                >
                  {show ? (
                    <EyeOff className="w-5 h-5" strokeWidth={1.5} />
                  ) : (
                    <Eye className="w-5 h-5" strokeWidth={1.5} />
                  )}
                </button>
              </div>
            </div>

            {otpRequired && (
              <div data-testid="otp-section">
                <label className="block text-xs tracking-overline uppercase text-[#8A8782] mb-2">
                  {useRecovery ? "Recovery Code" : "Google Authenticator Code"}
                </label>
                <input
                  type="text"
                  inputMode={useRecovery ? "text" : "numeric"}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  required
                  autoFocus
                  placeholder={useRecovery ? "XXXX-XXXX" : "123456"}
                  data-testid="admin-otp-input"
                  className="w-full border-b border-[#D1CEC2] bg-transparent text-2xl tracking-[0.3em] py-3 focus:border-[#2F2E2E] outline-none transition-colors"
                />
                <button
                  type="button"
                  onClick={() => {
                    setUseRecovery(!useRecovery);
                    setOtpCode("");
                  }}
                  data-testid="otp-recovery-toggle"
                  className="mt-3 text-xs text-[#8A8782] underline underline-offset-4 hover:text-[#1F1E1D]"
                >
                  {useRecovery ? "Use authenticator code instead" : "Lost your phone? Use a recovery code"}
                </button>
              </div>
            )}

            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                data-testid="admin-remember-checkbox"
                className="w-4 h-4 accent-[#2F2E2E]"
              />
              <span className="text-sm text-[#5C5A56]">
                Keep me signed in for 90 days
              </span>
            </label>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#2F2E2E] hover:bg-[#454341] text-white px-8 py-4 text-xs tracking-overline uppercase font-medium transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
